import { Button, ConfigProvider, Flex, Modal, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { showConfirmModal } from "components/modals/confirmModal";
import {
  ModalHeader,
  modalBlockLabelStyle,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import {
  UpcomingClassList,
  upcomingMetaStyle,
} from "components/modals/UpcomingClassList.component";
import { classDeleteCheckOptions } from "hooks/queries/classtype";
import { removalCountLabels } from "hooks/queries/user";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// A reward programme scoped to this class (System Review R6): its credits
// become unclaimable once the class is gone, so the pop-up says which
// programme to re-point before deleting.
const RewardWarning = ({ programs }: { programs?: string[] }) => {
  if (!programs || programs.length === 0) return null;
  return (
    <Text
      style={{
        display: "block",
        marginTop: 12,
        padding: "10px 12px",
        borderRadius: 8,
        background: colors.amberTint,
        color: colors.amberDark,
        fontSize: textPx(13),
      }}
    >
      {translateWithVars("class_delete.reward_warning", {
        programs: programs
          .map((p) => translate(`class_delete.program_${p}`))
          .join(", "),
      })}
    </Text>
  );
};

/**
 * Hapus Kelas asks the server first. A class with a session that hasn't
 * started can't be deleted: its sessions would stay on the calendar and in the
 * client app, bookable, with their credits held. The pop-up lists them, one
 * row per series (each opening its class), and offers to delete them all and
 * the class in one step (a second confirm states the numbers). Otherwise it
 * is the plain delete confirm. The server re-checks, so a session added in
 * the meantime is still refused.
 */
export const DeleteClassModal = ({
  open,
  classId,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  classId: string;
  pending: boolean;
  // removeUpcoming: delete every upcoming session first. onRefused refreshes
  // the list when the server refuses.
  onConfirm: (removeUpcoming: boolean, onRefused: () => void) => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery(classDeleteCheckOptions(classId, open));
  const upcoming = data?.upcoming_classes ?? [];

  const openClass = (scheduleId: string, occurrence: string) => {
    onClose();
    navigate(
      `${PATHS.SCHEDULE}/${scheduleId}?occurrence_timestamp=${encodeURIComponent(occurrence)}`
    );
  };

  const confirmRemoval = () => {
    if (!data) return;
    const { classes, bookings } = data.totals;
    showConfirmModal({
      tone: "error",
      title: translate("class_delete.remove_title"),
      content: translateWithVars(
        bookings > 0 ? "class_delete.remove_body_bookings" : "class_delete.remove_body",
        removalCountLabels(classes, bookings)
      ),
      confirmLabel: translate("class_delete.remove_confirm"),
      destructive: true,
      onConfirm: () => onConfirm(true, () => refetch()),
    });
  };

  const body = () => {
    if (isLoading || !data) {
      return (
        <Flex justify="center" style={{ padding: "28px 0" }}>
          <Spin />
        </Flex>
      );
    }
    if (upcoming.length > 0) {
      return (
        <>
          <ModalHeader tone="amber" title={translate("class_delete.blocked_title")} />
          <Text style={modalSubtitleStyle}>{translate("class_delete.blocked_body")}</Text>
          <Text style={modalBlockLabelStyle}>{translate("staff_status.upcoming.label")}</Text>
          <UpcomingClassList rows={upcoming} variant="class" onOpen={openClass} />
          {!data.can_remove_all && (
            <Text style={{ ...upcomingMetaStyle, display: "block", marginTop: 12 }}>
              {translate("messages.error.too_many_upcoming_classes")}
            </Text>
          )}
          <RewardWarning programs={data.reward_programs} />
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" gap={10} wrap="wrap" style={{ marginTop: 22 }}>
              <Button onClick={onClose} disabled={pending}>
                {translate("button.close")}
              </Button>
              <Button
                type="primary"
                danger
                loading={pending}
                disabled={!data.can_remove_all}
                onClick={confirmRemoval}
              >
                {translate("class_delete.remove_button")}
              </Button>
            </Flex>
          </ConfigProvider>
        </>
      );
    }
    return (
      <>
        <ModalHeader tone="error" title={translate("messages.warning.delete_class.title")} />
        <Text style={modalSubtitleStyle}>
          {translate("messages.warning.delete_class.content")}
        </Text>
        <RewardWarning programs={data.reward_programs} />
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={onClose} disabled={pending}>
              {translate("button.cancel")}
            </Button>
            <Button
              type="primary"
              danger
              loading={pending}
              onClick={() => onConfirm(false, () => refetch())}
            >
              {`${translate("button.delete")} ${translate("class")}`}
            </Button>
          </Flex>
        </ConfigProvider>
      </>
    );
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable
      maskClosable={!pending}
      width={480}
      onCancel={onClose}
      destroyOnClose
    >
      {body()}
    </Modal>
  );
};
